'use strict';

import PhotoSwipeLightbox from './photoswipe-lightbox.esm.min.js';
import PhotoSwipe from './photoswipe.esm.min.js';

/**
 * LilBontre-style product zoom:
 * - Desktop (>=768px): Drift.js inline hover magnify (inner-2, 3x)
 * - Mobile (<768px): PhotoSwipe fullscreen lightbox on tap
 */
class ChuttyPdpZoom extends HTMLElement {
  constructor() {
    super();
    this.type = this.dataset.type || 'inner_zoom_circle';
    this.zoomOption = this.dataset.zoomOption || 'inner-2';
    this.drifts = [];
    this.lightbox = null;
    this.onResponsive = this.onResponsive.bind(this);
    this.onGalleryClick = this.onGalleryClick.bind(this);
  }

  connectedCallback() {
    this.initLightBox();
    this.addEventListener('click', this.onGalleryClick);
    window.addEventListener('resize', this.onResponsive);
    window.addEventListener('load', this.onResponsive);
    this.onResponsive();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onGalleryClick);
    window.removeEventListener('resize', this.onResponsive);
    window.removeEventListener('load', this.onResponsive);
    this.destroyDrift();
    if (this.lightbox) {
      this.lightbox.destroy();
      this.lightbox = null;
    }
  }

  onResponsive() {
    const isDesktop = window.innerWidth >= 768;
    if (isDesktop) {
      this.destroyDrift();
      this.initDrift();
    } else {
      this.destroyDrift();
    }
  }

  initDrift() {
    if (typeof window.Drift === 'undefined' || this.drifts.length) return;

    const triggers = this.querySelectorAll('.drift-trigger');
    const externalArea = this.querySelector('.chutty-pdp__zoom-external-area');

    triggers.forEach((trigger) => {
      // Drift needs a zoom source URL on the trigger image.
      // Fallback to the media link href when data-zoom is absent.
      if (!trigger.getAttribute('data-zoom')) {
        const sourceLink = trigger.closest('.chutty-pdp__media-link');
        const zoomSrc = sourceLink?.getAttribute('href');
        if (zoomSrc) {
          trigger.setAttribute('data-zoom', zoomSrc);
        }
      }

      const inlineContainer = trigger.closest('[data-pane-container]');
      if (!inlineContainer) return;

      const drift = new window.Drift(trigger, {
        inlinePane: this.zoomOption === 'inner-2',
        zoomFactor: 3,
        containInline: externalArea || inlineContainer,
        paneContainer: inlineContainer,
        hoverBoundingBox: this.zoomOption === 'external',
        onShow: () => inlineContainer.classList.add('is-zoom-relative'),
        onHide: () => inlineContainer.classList.remove('is-zoom-relative'),
      });

      this.drifts.push(drift);
    });
  }

  destroyDrift() {
    this.drifts.forEach((drift) => drift.destroy());
    this.drifts = [];
    this.querySelectorAll('[data-pane-container]').forEach((el) => {
      el.classList.remove('is-zoom-relative');
    });
  }

  initLightBox() {
    if (this.lightbox) return;

    const lightbox = new PhotoSwipeLightbox({
      gallery: this,
      children: 'a.chutty-pdp__media-link',
      pswpModule: () => PhotoSwipe,
      counter: false,
      zoom: true,
      preloader: true,
      arrowPrev: false,
      arrowNext: false,
      close: false,
      loop: false,
      bgOpacity: 1,
    });

    lightbox.on('uiRegister', () => {
      const { pswp } = lightbox;

      pswp?.ui.registerElement({
        name: 'chutty-pswp-close',
        isButton: true,
        tagName: 'button',
        className: 'chutty-pswp__close',
        html: '&times;',
        onClick: () => pswp.close(),
      });

      pswp?.ui.registerElement({
        name: 'bottomBar',
        className: 'pswp__bottom-bar chutty-pswp__bottom-bar',
        appendTo: 'wrapper',
        onInit: (el, pswpInstance) => {
          const prev = document.createElement('button');
          prev.type = 'button';
          prev.className = 'pswp__button pswp__button--arrow--prev chutty-pswp__nav';
          prev.setAttribute('aria-label', 'Previous image');
          prev.innerHTML = '&#8249;';
          prev.onclick = () => pswpInstance.prev();
          el.appendChild(prev);

          const counter = document.createElement('span');
          counter.className = 'pswp__counter chutty-pswp__counter';
          pswpInstance.on('change', () => {
            counter.textContent = `${pswpInstance.currIndex + 1} / ${pswpInstance.getNumItems()}`;
          });
          el.appendChild(counter);

          const next = document.createElement('button');
          next.type = 'button';
          next.className = 'pswp__button pswp__button--arrow--next chutty-pswp__nav';
          next.setAttribute('aria-label', 'Next image');
          next.innerHTML = '&#8250;';
          next.onclick = () => pswpInstance.next();
          el.appendChild(next);
        },
      });
    });

    lightbox.init();
    this.lightbox = lightbox;
  }

  onGalleryClick(event) {
    if (window.innerWidth >= 768) {
      const link = event.target.closest('a.chutty-pdp__media-link');
      if (link && this.contains(link)) event.preventDefault();
      return;
    }

    // Ignore click synthesized after a swipe between images
    if (this.dataset.chuttySwiped === 'true') {
      event.preventDefault();
      return;
    }

    const link = event.target.closest('a.chutty-pdp__media-link');
    if (!link || !this.contains(link)) return;

    event.preventDefault();
    const position = parseInt(link.dataset.position || '1', 10);
    this.lightbox?.loadAndOpen(position - 1);
  }
}

customElements.define('chutty-pdp-zoom', ChuttyPdpZoom);
